import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { selectCartPageAggregates } from "~/features/cart/cart/redux/selectors/cartPageAggregates";
import {
  selectDisableCheckout,
  selectIsPnaLoading,
  selectInvalidItems,
  type InvalidCartItem,
} from "~/features/cart/cart/redux/selectors/cartPageSelectors";
import { selectPackageEntryMap } from "~/features/cart/cart/redux/selectors/cartSelectors";
import {
  switchProductVersion,
  updatePriceReferenceForCart as updatePriceReferenceAction,
} from "~/features/cart/cart/redux/actions/cartPageActions";
import { deleteSectionSaga } from "~/features/cart/cart/redux/actions/cartQuoteCommonActions";
import { insertAlternateRow, closeAlternateRow } from "~/features/cart/cartV2/redux/cartAlternatesSlice";
import {
  selectOrderablesViewModel,
  selectBundleViewModels,
} from "~/features/cart/cartV2/redux/selectors/cartTableSelectors";

export type { InvalidCartItem };

export function useB2BCartPageViewModel(externalDisableCheckout: boolean) {
  const dispatch = useDispatch();

  const aggregates = useSelector(selectCartPageAggregates);
  const disableCheckout = useSelector(selectDisableCheckout(externalDisableCheckout));
  const isPnaLoading = useSelector(selectIsPnaLoading);
  const orderablesViewModel = useSelector(selectOrderablesViewModel);
  const bundleViewModels = useSelector(selectBundleViewModels);
  const invalidItems = useSelector(selectInvalidItems);
  const packageEntryMap = useSelector(selectPackageEntryMap);
  const packageEntryIds = Object.keys(packageEntryMap).map(Number);

  const orderablesTableId = orderablesViewModel.tableId;
  const orderablesProductGroupNumbers = orderablesViewModel.rows
    .filter((r): r is Extract<typeof r, { kind: "entry" }> => r.kind === "entry")
    .map((r) => r.productGroupNumber);

  const handleInsertAlternateRow = useCallback(
    (input: { anchorProductGroupNumber: number | null; productCode: string; designation: string }) => {
      dispatch(insertAlternateRow({ tableId: orderablesTableId, ...input }));
    },
    [dispatch, orderablesTableId],
  );

  const handleCloseAlternateRow = useCallback(
    (rowId: string) => {
      dispatch(
        closeAlternateRow({ tableId: orderablesTableId, rowId, productGroupNumbers: orderablesProductGroupNumbers }),
      );
    },
    [dispatch, orderablesTableId, orderablesProductGroupNumbers],
  );

  const handleDeleteSection = useCallback(
    (quoteNumber?: string) => {
      dispatch(deleteSectionSaga(quoteNumber ?? ""));
    },
    [dispatch],
  );

  const handleUpdatePriceReferenceForCart = useCallback(
    (priceReference: string, onSuccess: (count: number) => void) => {
      dispatch(updatePriceReferenceAction(priceReference, packageEntryIds, onSuccess));
    },
    [dispatch, packageEntryIds],
  );

  const handleSwitchProductVersionForCart = useCallback(() => {
    dispatch(switchProductVersion());
  }, [dispatch]);

  return {
    aggregates,
    disableCheckout,
    isPnaLoading,
    orderablesViewModel,
    bundleViewModels,
    invalidItems,
    handleInsertAlternateRow,
    handleCloseAlternateRow,
    handleDeleteSection,
    handleUpdatePriceReferenceForCart,
    handleSwitchProductVersionForCart,
  };
}
